import { MAX_CATCHUP_MS, TICK_MS, type Vec2, lerp } from '@d2/core';

/**
 * Where to draw an entity whose input this client does not hold.
 *
 * The server replicates a walking entity's position **every tick** — 20 Hz,
 * stamped with `WorldState.tick` (`packages/schema/src/state.ts`). The client
 * draws at 60 fps. So two frames in three have no sample of their own, and
 * something has to fill them: drawing the newest sample as it arrives renders a
 * smooth walk as three still frames and a jump, twenty times a second.
 *
 * `ARCHITECTURE.md` fixes the answer — the client "render[s] at 60 fps,
 * interpolating remote entities behind a ~100 ms buffer and predicting only its
 * own movement". This module is that buffer: samples in, a point out, and no
 * opinion about anything else.
 *
 * ## Only for entities whose input you do not hold
 *
 * The local player is **not** interpolated. It runs core's `stepWalker` on its
 * own input the moment the input happens (`./navigation`), because a player
 * watching their own click take 100 ms to start is a player who thinks the game
 * is broken. That is prediction, it has different rules — a route, a fixed step,
 * a reconciliation against server truth — and it lives elsewhere on purpose.
 * Interpolation is the other case: no input to run, no route worth re-deriving
 * (`state.ts` explains why the client does not re-path remote entities), just a
 * stream of authoritative positions and the gaps between them.
 *
 * ## The time base
 *
 * One timeline, in milliseconds: a sample's stamp is `tick * TICK_MS`, and
 * `positionAt` takes a render time on that same timeline. Nothing here reads a
 * clock — time is a parameter, which is the only reason a walk can be replayed
 * frame by frame in a unit test.
 *
 * Mapping the browser's clock onto the server's timeline is the *caller's* job
 * and belongs to the networking pass: `newestTick` plus the milliseconds since
 * that sample landed is the whole of the simple estimator, and a jitter-robust
 * one is `TODO.md` row 160. Keeping it out here is what makes this module
 * usable by that pass without change.
 *
 * ## The four decisions, and why
 *
 * **A gap in the stream — interpolate across it.** A missing tick leaves two
 * samples further apart than usual, and the segment between them is still
 * bounded at *both* ends by positions the server actually simulated. Nothing is
 * invented: the entity is drawn somewhere it demonstrably passed through, and a
 * corner cut inside the gap is at most a walk-speed fraction of a cell that the
 * next sample resolves without a snap. Holding instead would freeze the entity
 * mid-stride and then jump it the full gap.
 *
 * **Out-of-order and duplicate samples — dropped, defensively.** Colyseus rides
 * one TCP connection per room, so the transport cannot reorder patches and this
 * case does not arise; `receiveSample` still ignores anything at or before the
 * newest tick, because that is one comparison and it turns "samples are ordered"
 * from an assumption the bracketing quietly depends on into an invariant the
 * buffer holds itself. A stale sample carries no information anyway — the client
 * has already rendered past it.
 *
 * **Nothing buffered yet — draw nothing, then draw the one sample exactly.** An
 * empty buffer answers `null`: there is no honest position for an entity that
 * has never reported one, and inventing an origin would flash every joining
 * player through cell (0, 0). With exactly one sample the delay is ignored and
 * that sample is drawn as-is; it cannot be interpolated and must not be
 * extrapolated, so a player who joined mid-walk stands still for up to the delay
 * and then walks. A brief stillness is invisible; a guessed position that snaps
 * is not.
 *
 * **The buffer runs dry — hold the newest sample.** When the render clock
 * outruns the stream the entity stops where it was last seen. Extrapolating is
 * the tempting alternative, and `walking` + `destinationX/Y` are even on the
 * wire to bound it — but it invents motion the server never sent, and it is
 * wrong exactly when it matters: an entity that arrived, was blocked by a pack
 * (`TODO.md` #140), or was knocked back gets walked forward and then snapped
 * back the moment truth lands. A hold is never undone, only resumed; it is the
 * same reasoning as core's capped catch-up, where the next authoritative state
 * *is* the correction rather than something to pre-empt.
 *
 * Positions are in **cell coordinates**, like all of core. The renderer projects
 * (`./iso`); no pixel constant belongs in here.
 */

/**
 * One replicated position, stamped with the server tick it was produced on.
 *
 * Declared here structurally rather than imported from `@d2/schema`, which owns
 * the wire format: `apps/web` does not depend on that package, and a buffer that
 * took a `PlayerState` would be untestable without `@colyseus/schema` and
 * unusable for anything but a player. Any entity with a tick and a position
 * fits, which is what a monster will be.
 */
export interface PositionSample {
  /** `WorldState.tick` — the server tick this position was simulated on. A whole, non-negative number. */
  readonly tick: number;
  /** Continuous position in cell coordinates. */
  readonly position: Vec2;
}

/**
 * A remote entity's recent history, and how far behind it is drawn.
 *
 * Immutable, like everything it composes: `receiveSample` returns a new buffer
 * rather than pushing into this one, so a frame can never observe a half-applied
 * update and a caller can keep one per entity in a plain map.
 */
export interface InterpolationBuffer {
  /** How far behind the newest sample this entity is rendered, in milliseconds. */
  readonly delayMs: number;
  /** Oldest first, strictly increasing `tick`. */
  readonly samples: readonly PositionSample[];
}

/**
 * How many samples the render clock sits behind the newest one. **Two.**
 *
 * The delay is what buys the smoothing: rendering the newest sample the instant
 * it lands leaves nothing to interpolate *towards*, so every hiccup in delivery
 * is a stutter on screen. Sitting deliberately behind means the frame being
 * drawn is nearly always bracketed by two samples that have both already
 * arrived.
 *
 * Two samples rather than one because a single one absorbs no jitter at all —
 * one patch a millisecond late empties the buffer — and rather than three
 * because every extra sample is 50 ms of added lag on seeing what other players
 * are doing, which is a co-op game's actual feel.
 */
export const INTERPOLATION_DELAY_TICKS = 2;

/**
 * The delay in milliseconds: exactly 100, the `~100 ms` of `ARCHITECTURE.md`.
 *
 * Derived from `TICK_MS` rather than written as `100`, so it stays a whole
 * number of samples if the tick rate ever moves. That it *is* a whole number is
 * the convenient part: the render clock lands on sample boundaries, so a client
 * with a perfectly synchronised clock and no jitter interpolates between exactly
 * the two samples it expects.
 */
export const INTERPOLATION_DELAY_MS = INTERPOLATION_DELAY_TICKS * TICK_MS;

/**
 * How much history a buffer keeps behind its newest sample.
 *
 * The delay is the part that is *needed*: the render clock is pointed there, so
 * anything newer than `delayMs` back is what the bracketing reads. The slack on
 * top is `MAX_CATCHUP_MS` — core's own answer to "how far behind may a client
 * fall before catching up is the wrong idea". A client further behind than that
 * is past what smoothing can hide, and the honest thing is to draw the oldest
 * sample retained and let the stream pull it forward, which is exactly what the
 * clamp in `positionAt` does. Retaining more would buy a smoother recovery from
 * a stall that core has already decided not to replay.
 */
const retentionMs = (delayMs: number): number => delayMs + MAX_CATCHUP_MS;

/**
 * The most samples a buffer can hold — 8 at the default delay.
 *
 * The bound is a consequence of the retention window and the fixed tick rate,
 * not a cap anybody enforces: samples arrive at most one per tick, and every one
 * older than `retentionMs` behind the newest is evicted as it lands. Exported so
 * the bound can be asserted rather than assumed.
 */
export const bufferedSampleLimit = (delayMs: number = INTERPOLATION_DELAY_MS): number =>
  Math.floor(retentionMs(delayMs) / TICK_MS) + 1;

/** A tick's place on the render timeline. The one conversion between the two. */
export const tickTimeMs = (tick: number): number => tick * TICK_MS;

const assertDelay = (delayMs: number): void => {
  // Phrased as a negated comparison so NaN — a delay derived from a bad
  // subtraction — is rejected along with the negatives.
  if (!(Number.isFinite(delayMs) && delayMs >= 0)) {
    throw new RangeError(`An interpolation delay must be a finite, non-negative number of milliseconds, received ${delayMs}`);
  }
};

const assertTick = (tick: number): void => {
  if (!(Number.isInteger(tick) && tick >= 0)) {
    throw new RangeError(`A sample's tick must be a whole, non-negative number, received ${tick}`);
  }
};

/** An entity nothing has been heard from yet. */
export const createBuffer = (delayMs: number = INTERPOLATION_DELAY_MS): InterpolationBuffer => {
  assertDelay(delayMs);

  return { delayMs, samples: [] };
};

/** The newest tick buffered, or `null` while nothing has arrived. */
export const newestTick = (buffer: InterpolationBuffer): number | null => buffer.samples.at(-1)?.tick ?? null;

/**
 * Files a sample and drops whatever it just aged out.
 *
 * Two rules, both stated above: a sample at or before the newest tick is
 * ignored, and history older than `retentionMs` behind the arriving sample is
 * evicted — which is what stops a buffer that is written twenty times a second
 * for an hour from being an hour long.
 */
export const receiveSample = (buffer: InterpolationBuffer, sample: PositionSample): InterpolationBuffer => {
  assertTick(sample.tick);

  const newest = newestTick(buffer);
  if (newest !== null && sample.tick <= newest) return buffer;

  const oldestKeptMs = tickTimeMs(sample.tick) - retentionMs(buffer.delayMs);
  const kept = buffer.samples.filter((held) => tickTimeMs(held.tick) >= oldestKeptMs);

  return { ...buffer, samples: [...kept, sample] };
};

/**
 * Where to draw the entity at `renderTimeMs` — `null` when nothing is known
 * about it yet.
 *
 * The query is pure: the same render time always gives the same point, which is
 * what lets a frame be redrawn, replayed or tested. It reads `delayMs` back off
 * the buffer, so the render clock a caller passes is its own, unadjusted.
 *
 * Both ends are clamps rather than extrapolations, and each is one of the
 * decisions above: past the newest sample the entity holds, before the oldest it
 * waits. `lerp` is core's — unclamped by design, and given a factor in `[0, 1)`
 * here — so the two sides of the wire cannot blend differently.
 */
export const positionAt = (buffer: InterpolationBuffer, renderTimeMs: number): Vec2 | null => {
  if (!Number.isFinite(renderTimeMs)) {
    throw new RangeError(`A render time must be a finite number of milliseconds, received ${renderTimeMs}`);
  }

  const { samples } = buffer;
  const oldest = samples[0];
  if (oldest === undefined) return null;

  const targetMs = renderTimeMs - buffer.delayMs;
  // The newest sample at or before the target, and the one after it. Missing
  // either is a real answer rather than an edge case: no sample before the
  // target means the client is still behind the oldest one buffered, and none
  // after means the buffer has run dry. Both fall through to the same hold.
  const behindIndex = samples.findLastIndex((held) => tickTimeMs(held.tick) <= targetMs);
  const behind = samples[behindIndex] ?? oldest;
  const ahead = samples[behindIndex + 1] ?? behind;

  const behindMs = tickTimeMs(behind.tick);
  const spanMs = tickTimeMs(ahead.tick) - behindMs;

  return spanMs === 0 ? behind.position : lerp(behind.position, ahead.position, (targetMs - behindMs) / spanMs);
};
